import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Animated,
  LayoutChangeEvent,
  PanResponder,
  Platform,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import { isTablet } from 'react-native-device-info';
import { GTFonts, useLayoutSize, useTheme } from '../../theme';
import { fs, iconSize, s, v } from '../../utils/libs';
import { useAppSelector } from '../../redux/helper';
import { selectEnableQuickSell } from '../../redux/SettingsSlice';

const STORAGE_KEY = 'backoffice_pos_fab_position_v1';
const EDGE_MARGIN = s(12);
/** Movement above this (in px) turns a press into a drag, so no navigation fires. */
const TAP_SLOP = 6;
const DEFAULT_RATIO = { xRatio: 1, yRatio: 0.72 };

type Ratio = { xRatio: number; yRatio: number };
type Bounds = { minX: number; maxX: number; minY: number; maxY: number };

const clamp = (n: number, min: number, max: number) =>
  Math.min(Math.max(n, min), max);

/**
 * Draggable shortcut from any Back Office tab to the employee POS product list.
 * Position snaps to the nearest side on release and is persisted per device.
 */
const BackOfficePosFab: React.FC = () => {
  const { theme } = useTheme();
  const navigation = useNavigation();
  const insets = useSafeAreaInsets();
  const { useSidebarNav } = useLayoutSize();
  const enableQuickSell = useAppSelector(selectEnableQuickSell);
  const windowSize = useWindowDimensions();
  const [area, setArea] = useState({
    width: windowSize.width,
    height: windowSize.height,
  });

  const size = s(isTablet() ? 56 : 48);
  const styles = useMemo(() => createStyles(theme, size), [theme, size]);

  const [ready, setReady] = useState(false);
  const [dragging, setDragging] = useState(false);

  const pos = useRef(new Animated.ValueXY({ x: 0, y: 0 })).current;
  const posRef = useRef({ x: 0, y: 0 });
  const dragStartRef = useRef({ x: 0, y: 0 });
  const movedRef = useRef(false);
  const ratioRef = useRef<Ratio>(DEFAULT_RATIO);

  const bounds = useMemo<Bounds>(() => {
    // Keep clear of the Back Office header at the top and the tab bar at the bottom.
    const topClearance = v(64);
    const bottomClearance = useSidebarNav ? v(16) : v(88) + insets.bottom;
    const minY = topClearance;
    const maxY = Math.max(minY, area.height - size - bottomClearance);
    return {
      minX: EDGE_MARGIN,
      maxX: Math.max(EDGE_MARGIN, area.width - size - EDGE_MARGIN),
      minY,
      maxY,
    };
  }, [area.width, area.height, size, insets.bottom, useSidebarNav]);

  const boundsRef = useRef(bounds);
  boundsRef.current = bounds;

  const applyRatio = useCallback(
    (ratio: Ratio) => {
      const b = boundsRef.current;
      pos.setValue({
        x: b.minX + (b.maxX - b.minX) * ratio.xRatio,
        y: b.minY + (b.maxY - b.minY) * ratio.yRatio,
      });
    },
    [pos],
  );

  useEffect(() => {
    const id = pos.addListener(value => {
      posRef.current = value;
    });
    return () => pos.removeListener(id);
  }, [pos]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let ratio = DEFAULT_RATIO;
      try {
        const raw = await AsyncStorage.getItem(STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        if (
          Number.isFinite(parsed?.xRatio) &&
          Number.isFinite(parsed?.yRatio)
        ) {
          ratio = {
            xRatio: clamp(parsed.xRatio, 0, 1),
            yRatio: clamp(parsed.yRatio, 0, 1),
          };
        }
      } catch {
        // Fall back to the default corner.
      }
      if (cancelled) return;
      ratioRef.current = ratio;
      applyRatio(ratio);
      setReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [applyRatio]);

  // Re-anchor on rotation / window resize so the button stays inside the screen.
  useEffect(() => {
    if (!ready) return;
    applyRatio(ratioRef.current);
  }, [bounds, ready, applyRatio]);

  const persistPosition = useCallback((x: number, y: number) => {
    const b = boundsRef.current;
    const spanX = b.maxX - b.minX;
    const spanY = b.maxY - b.minY;
    const ratio: Ratio = {
      xRatio: spanX > 0 ? clamp((x - b.minX) / spanX, 0, 1) : 0,
      yRatio: spanY > 0 ? clamp((y - b.minY) / spanY, 0, 1) : 0,
    };
    ratioRef.current = ratio;
    AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(ratio)).catch(() => { });
  }, []);

  const goToPosProducts = useCallback(() => {
    // BackOffice and MainScreen are siblings in AppStack, but this component can
    // be mounted below nested navigators, so walk up until MainScreen is known.
    let nav: any = navigation;
    while (nav) {
      const names: string[] = nav.getState?.()?.routeNames ?? [];
      if (names.includes('MainScreen')) {
        nav.navigate('MainScreen', {
          screen: 'ProductsScreen',
          params: { from: 'BackOffice' },
        });
        return;
      }
      nav = nav.getParent?.();
    }
    (navigation as any).navigate('MainScreen', { screen: 'ProductsScreen' });
  }, [navigation]);

  const snapToNearestSide = useCallback(() => {
    const b = boundsRef.current;
    const midX = (b.minX + b.maxX) / 2;
    const targetX = posRef.current.x < midX ? b.minX : b.maxX;
    const targetY = clamp(posRef.current.y, b.minY, b.maxY);
    Animated.spring(pos, {
      toValue: { x: targetX, y: targetY },
      useNativeDriver: false,
      friction: 8,
      tension: 90,
    }).start(({ finished }) => {
      if (finished) persistPosition(targetX, targetY);
    });
  }, [pos, persistPosition]);

  const panResponder = useMemo(
    () =>
      PanResponder.create({
        onStartShouldSetPanResponder: () => true,
        onMoveShouldSetPanResponder: (_e, g) =>
          Math.abs(g.dx) > 2 || Math.abs(g.dy) > 2,
        onPanResponderGrant: () => {
          dragStartRef.current = { ...posRef.current };
          movedRef.current = false;
          setDragging(true);
        },
        onPanResponderMove: (_e, g) => {
          if (Math.hypot(g.dx, g.dy) > TAP_SLOP) movedRef.current = true;
          const b = boundsRef.current;
          pos.setValue({
            x: clamp(dragStartRef.current.x + g.dx, b.minX, b.maxX),
            y: clamp(dragStartRef.current.y + g.dy, b.minY, b.maxY),
          });
        },
        onPanResponderRelease: () => {
          setDragging(false);
          if (!movedRef.current) {
            goToPosProducts();
            return;
          }
          snapToNearestSide();
        },
        onPanResponderTerminate: () => {
          setDragging(false);
          snapToNearestSide();
        },
      }),
    [pos, goToPosProducts, snapToNearestSide],
  );

  const handleAreaLayout = useCallback((e: LayoutChangeEvent) => {
    const { width, height } = e.nativeEvent.layout;
    setArea(prev =>
      Math.abs(prev.width - width) < 1 && Math.abs(prev.height - height) < 1
        ? prev
        : { width, height },
    );
  }, []);

  if (!enableQuickSell) {
    return null;
  }

  return (
    <View
      style={StyleSheet.absoluteFill}
      pointerEvents="box-none"
      onLayout={handleAreaLayout}>
      {ready ? (
        <Animated.View
          {...panResponder.panHandlers}
          accessibilityRole="button"
          accessibilityLabel="Make a Sale"
          style={[
            styles.fab,
            dragging && styles.fabDragging,
            {
              transform: [{ translateX: pos.x }, { translateY: pos.y }],
            },
          ]}>
          {/* <MaterialCommunityIcons
            name="cart-outline"
            size={iconSize(isTablet() ? 26 : 24)}
            color={theme.colors.textInverse}
          /> */}
          <Text style={styles.label} numberOfLines={1}>
            Quick
          </Text>
          <Text style={styles.label} numberOfLines={1}>
            Sell
          </Text>
        </Animated.View>
      ) : null}
    </View>
  );
};

export default BackOfficePosFab;

const createStyles = (theme: any, size: number) =>
  StyleSheet.create({
    fab: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: size,
      height: size,
      borderRadius: theme.borderRadius.md,
      backgroundColor: theme.colors.primaryDark,
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 60,
      ...theme.shadows.lg,
      ...(Platform.OS === 'web' && { cursor: 'grab', userSelect: 'none' }),
    } as any,
    fabDragging: {
      opacity: 0.9,
      ...(Platform.OS === 'web' && { cursor: 'grabbing' }),
    } as any,
    label: {
      color: theme.colors.textInverse,
      fontFamily: GTFonts.bold,
      fontSize: fs(12),
      lineHeight: fs(15),
      marginTop: v(2),
      letterSpacing: 0.3,
    },
  });
